import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Lock, Check, MessageCircle, Heart, ChevronLeft, ChevronRight, X } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import {
  DCard,
  SectionHeader,
  Avatar,
  Pill,
  ProgressBar,
  HeroCard,
  MagazineGrid,
  EmptyState,
  LoadError,
} from "@/components/dryvn";
import { AddPanel, LifeField, LifeInput, LifeSelect, SubmitButton } from "@/components/life-forms";
import type { FamilyEvent } from "@/data/types";
// Screen migration (2026-07-05): live data via hooks. lastBroadcast +
// DRYVN_NUMBER stay as fixtures — Circle broadcast is Phase-12 DEFER
// with no empty-state design for a single-object card (PORT_NOTES).
import { lastBroadcast, DRYVN_NUMBER } from "@/data/mock";
import {
  useFamily,
  useFamilyEvents,
  useAddFamilyEvent,
  useUpdateFamilyEvent,
  useDeleteFamilyEvent,
  useFamilyMembers,
  useAddFamilyMember,
  useUpdateFamilyMember,
  useDeleteFamilyMember,
  useHomework,
  useAddHomework,
  useRewardCharts,
  useBehaviourLog,
  useElders,
  useMe,
  usePendingInvites,
  useInviteMember,
  useRevokeInvite,
  type FamilyMemberInput,
  type PendingInvite,
} from "@/lib/queries";
import { memberInitials, memberColor, type WireMember } from "@/lib/adapt";
// LANE 2 live-gating (2026-07-10): the Circle broadcast card renders
// fixtures (lastBroadcast) and the placeholder DRYVN_NUMBER, and the
// Elders / Homework / Reward-charts / Behaviour-log sections are
// Phase-12 DEFER with no v1 backend (empty [] in live). All are
// demo-only. Live keeps the household roster and the real week calendar.
import { IS_DEMO } from "@/lib/mode";
import { CollapsibleList } from "@/components/collapsible-list";

export const Route = createFileRoute("/family")({
  head: () => ({
    meta: [
      { title: "Family — Dryvn Life" },
      {
        name: "description",
        content: "The family's week, homework, rewards, elder check-ins, and the circle.",
      },
    ],
  }),
  component: Family,
});

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Event chips a single calendar cell renders before it shows "+N more".
 * A grid cell cannot grow without dragging its whole row with it, so the
 * cap is fixed rather than expandable — the day view is the full list.
 */
const DAY_CELL_CAP = 3;

const toneStyle: Record<string, string> = {
  positive: "var(--dryvn-primary)",
  concerning: "var(--dryvn-warm-deep)",
  neutral: "var(--dryvn-grey)",
  formal: "var(--dryvn-amber)",
};

// Form eventKind (backend enum) → calendar view kind (adapt.ts maps this
// on read; mirrored here for optimistic inserts).
const WIRE_TO_VIEW: Record<string, FamilyEvent["kind"]> = {
  family: "family",
  school: "school",
  sports: "sports",
  carpool: "carpool",
  appointment: "meeting",
};

// Reverse: view kind → the form/wire value, so the edit form re-seeds the
// Kind select from a stored event ("meeting" → "appointment").
const VIEW_TO_WIRE: Record<FamilyEvent["kind"], string> = {
  family: "family",
  school: "school",
  sports: "sports",
  carpool: "carpool",
  meeting: "appointment",
};

type RosterMember = { id: string; name: string; initials: string; color: string };

const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "family", label: "Family" },
  { value: "school", label: "School" },
  { value: "sports", label: "Sports" },
  { value: "carpool", label: "Carpool" },
  { value: "appointment", label: "Appointment" },
];

// ── Local-time date helpers (the calendar is a wall-clock grid) ──────────────
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
function atStartOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); // back up to Monday
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function addMonths(d: Date, n: number): Date {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n, 1);
  return x;
}
// "YYYY-MM-DD" + "HH:MM" → an ISO timestamp at that local wall-clock instant,
// so a web-added/edited event lands on the day the user picked.
function isoFromDateTime(date: string, time: string): string {
  const [y, mo, dd] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  return new Date(y ?? 1970, (mo ?? 1) - 1, dd ?? 1, h ?? 9, mi ?? 0, 0, 0).toISOString();
}
// "YYYY-MM-DD" → "Thu, Aug 6" for the School list rows (local wall-clock).
function shortDateLabel(date: string): string {
  const [y, mo, dd] = date.split("-").map(Number);
  return new Date(y ?? 1970, (mo ?? 1) - 1, dd ?? 1).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// The household members invited to an event → a display label. Empty means
// nobody was singled out, so the whole household is involved.
function attendeeNames(ids: string[], roster: RosterMember[]): string[] {
  return ids
    .map((id) => roster.find((m) => m.id === id)?.name)
    .filter((n): n is string => Boolean(n));
}

/**
 * Multi-select of household members. No selection means "Everyone" — the
 * founder mandate: the calendar shows everyone by default, but specific
 * people can be invited to specific events. Chips reuse the Avatar + line/
 * primary tokens (no new visual language).
 */
function MemberPicker({
  roster,
  value,
  onChange,
}: {
  roster: RosterMember[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {roster.map((m) => {
        const on = value.includes(m.id);
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => toggle(m.id)}
            aria-pressed={on}
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors"
            style={{
              borderColor: on ? "var(--dryvn-primary)" : "var(--dryvn-line)",
              backgroundColor: on
                ? "color-mix(in oklab, var(--dryvn-primary) 12%, var(--dryvn-card))"
                : "var(--dryvn-card)",
              color: "var(--dryvn-ink)",
            }}
          >
            <Avatar initials={m.initials} color={m.color} size={16} />
            {m.name}
            {on && <Check className="h-3 w-3" style={{ color: "var(--dryvn-primary)" }} />}
          </button>
        );
      })}
      {value.length === 0 && (
        <span className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
          Everyone's involved — tap a name to invite just them.
        </span>
      )}
    </div>
  );
}

// Same concept as the Pro dashboard's Team roster, targeted at families:
// members are first-class profiles (name, relationship, birthday, contact),
// managed here and reused everywhere (calendar attendees, check-ins).
const RELATIONSHIP_OPTIONS: { value: string; label: string }[] = [
  { value: "parent", label: "Parent" },
  { value: "spouse", label: "Spouse" },
  { value: "partner", label: "Partner" },
  { value: "son", label: "Son" },
  { value: "daughter", label: "Daughter" },
  { value: "child", label: "Child" },
  { value: "grandmother", label: "Grandmother" },
  { value: "grandfather", label: "Grandfather" },
  { value: "other", label: "Other" },
];

const relationshipLabel = (value: string): string =>
  RELATIONSHIP_OPTIONS.find((o) => o.value === value)?.label ?? value;

type MemberFormValues = {
  name: string;
  relationship: string;
  dateOfBirth: string;
  phone: string;
  email: string;
};

/**
 * Family roles for app access, mapped onto the platform's role enum. The
 * same consent-gated invite flow as the Pro dashboard's Team — one flow,
 * one table, two callers — with family-language labels. 'viewer' is the
 * child/teen role: family surfaces only, no money/private/config access
 * (enforced server-side, not just hidden).
 */
const FAMILY_ROLE_OPTIONS: { value: string; label: string; hint: string }[] = [
  {
    value: "admin",
    label: "Parent / guardian",
    hint: "Runs the household — everything except billing.",
  },
  { value: "member", label: "Adult", hint: "Can see and add across the household." },
  {
    value: "viewer",
    label: "Teen / child",
    hint: "Family surfaces only — no money, vault, or settings.",
  },
];

const familyRoleLabel = (value: string): string =>
  FAMILY_ROLE_OPTIONS.find((o) => o.value === value)?.label ?? value;

/**
 * Owner-only invite form. Email only by design: the invited person gets a
 * consent email first, and their phone + SMS opt-in are collected from THEM
 * on the consent page — never entered here on their behalf.
 */
function InviteForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (v: { email: string; proposedRole: string }) => void;
}) {
  const [email, setEmail] = useState("");
  const [proposedRole, setProposedRole] = useState("member");
  const hint = FAMILY_ROLE_OPTIONS.find((o) => o.value === proposedRole)?.hint ?? "";

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const v = email.trim();
        if (!v) return;
        onSubmit({ email: v, proposedRole });
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <LifeField label="Their email">
          <LifeInput
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="them@example.com"
          />
        </LifeField>
        <LifeField label="Their role">
          <LifeSelect value={proposedRole} onChange={(e) => setProposedRole(e.target.value)}>
            {FAMILY_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </LifeSelect>
        </LifeField>
      </div>
      <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
        {hint} They'll get an email asking for their consent first — nothing is shared and no texts
        are sent until they accept and opt in themselves.
      </p>
      <SubmitButton disabled={!email.trim() || pending}>
        {pending ? "Sending…" : "Send invite"}
      </SubmitButton>
      {error && (
        <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
          {error}
        </p>
      )}
    </form>
  );
}

/** One pending invite row: who, as what, where it stands, and Revoke. */
function PendingInviteRow({
  invite,
  onRevoke,
  revoking,
}: {
  invite: PendingInvite;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const [confirm, setConfirm] = useState(false);
  return (
    <li className="flex items-center gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm" style={{ color: "var(--dryvn-ink)" }}>
          {invite.email ?? "(no email)"}
        </p>
        <p className="text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
          {familyRoleLabel(invite.proposedRole)}
        </p>
      </div>
      <Pill tone={invite.consentAcceptedAt ? "life" : "muted"}>
        {invite.consentAcceptedAt ? "consented — finishing setup" : "waiting on consent"}
      </Pill>
      {confirm ? (
        <span className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={onRevoke}
            disabled={revoking}
            className="rounded-full px-3 py-1 font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: "var(--dryvn-warm-deep)" }}
          >
            {revoking ? "Revoking…" : "Confirm"}
          </button>
          <button
            type="button"
            onClick={() => setConfirm(false)}
            style={{ color: "var(--dryvn-grey)" }}
          >
            Cancel
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setConfirm(true)}
          className="text-xs"
          style={{ color: "var(--dryvn-warm-deep)" }}
        >
          Revoke
        </button>
      )}
    </li>
  );
}

/**
 * Shared add/edit form for a family member profile. Mirrors EventForm's
 * shape: same field components, submit + optional two-step Delete, error
 * line owned by the caller. Empty optionals are omitted from the wire body
 * (the backend rejects empty strings by design).
 */
function MemberForm({
  initial,
  submitLabel,
  pending,
  error,
  onSubmit,
  onDelete,
  deleting,
}: {
  initial: MemberFormValues;
  submitLabel: string;
  pending: boolean;
  error: string | null;
  onSubmit: (v: FamilyMemberInput) => void;
  onDelete?: () => void;
  deleting?: boolean;
}) {
  const [name, setName] = useState(initial.name);
  const [relationship, setRelationship] = useState(initial.relationship);
  const [dateOfBirth, setDateOfBirth] = useState(initial.dateOfBirth);
  const [phone, setPhone] = useState(initial.phone);
  const [email, setEmail] = useState(initial.email);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const n = name.trim();
        if (!n) return;
        onSubmit({
          name: n,
          relationship,
          ...(dateOfBirth ? { dateOfBirth } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(email.trim() ? { email: email.trim() } : {}),
        });
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <LifeField label="Name">
          <LifeInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ellie, Grandma Rose…"
          />
        </LifeField>
        <LifeField label="Relationship">
          <LifeSelect value={relationship} onChange={(e) => setRelationship(e.target.value)}>
            {RELATIONSHIP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </LifeSelect>
        </LifeField>
      </div>
      <LifeField label="Birthday (optional)">
        <LifeInput
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
        />
      </LifeField>
      <div className="grid grid-cols-2 gap-3">
        <LifeField label="Phone (optional)">
          <LifeInput
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="604 555 0123"
          />
        </LifeField>
        <LifeField label="Email (optional)">
          <LifeInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="them@example.com"
          />
        </LifeField>
      </div>
      <div className="flex items-center gap-3">
        <SubmitButton disabled={!name.trim() || pending}>
          {pending ? "Saving…" : submitLabel}
        </SubmitButton>
        {onDelete &&
          (confirmDelete ? (
            <span className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="rounded-full px-3 py-1.5 font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: "var(--dryvn-warm-deep)" }}
              >
                {deleting ? "Removing…" : "Confirm remove"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                style={{ color: "var(--dryvn-grey)" }}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="text-xs"
              style={{ color: "var(--dryvn-warm-deep)" }}
            >
              Remove from household
            </button>
          ))}
      </div>
      {error && (
        <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
          {error}
        </p>
      )}
    </form>
  );
}

type EventFormValues = {
  title: string;
  date: string;
  time: string;
  kind: string;
  attendeeIds: string[];
};

/**
 * Shared add/edit form. The add flow and the tap-to-edit flow render the
 * SAME fields (founder: events must be editable); edit additionally shows a
 * two-step Delete. Reset on identity change is handled by the caller via a
 * React `key`.
 */
function EventForm({
  roster,
  initial,
  submitLabel,
  pending,
  error,
  onSubmit,
  onDelete,
  deleting,
}: {
  roster: RosterMember[];
  initial: EventFormValues;
  submitLabel: string;
  pending: boolean;
  error: string | null;
  onSubmit: (v: EventFormValues) => void;
  onDelete?: () => void;
  deleting?: boolean;
}) {
  const [title, setTitle] = useState(initial.title);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [kind, setKind] = useState(initial.kind);
  const [attendeeIds, setAttendeeIds] = useState<string[]>(initial.attendeeIds);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const t = title.trim();
        if (!t) return;
        onSubmit({ title: t, date, time, kind, attendeeIds });
      }}
    >
      <LifeField label="What's happening">
        <LifeInput
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Soccer practice, dentist…"
        />
      </LifeField>
      <div className="grid grid-cols-2 gap-3">
        <LifeField label="Date">
          <LifeInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </LifeField>
        <LifeField label="Time">
          <LifeInput type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </LifeField>
      </div>
      <LifeField label="Kind">
        <LifeSelect value={kind} onChange={(e) => setKind(e.target.value)}>
          {KIND_OPTIONS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </LifeSelect>
      </LifeField>
      {/* Not a <label> — MemberPicker holds interactive buttons. */}
      <div>
        <span
          className="mb-1 block font-mono text-[10px] uppercase tracking-wider"
          style={{ color: "var(--dryvn-grey)" }}
        >
          Who's involved
        </span>
        <MemberPicker roster={roster} value={attendeeIds} onChange={setAttendeeIds} />
      </div>
      <div className="flex items-center gap-3">
        <SubmitButton disabled={!title.trim() || pending}>
          {pending ? "Saving…" : submitLabel}
        </SubmitButton>
        {onDelete &&
          (confirmDelete ? (
            <span className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="rounded-full px-3 py-1.5 font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: "var(--dryvn-warm-deep)" }}
              >
                {deleting ? "Deleting…" : "Confirm delete"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                style={{ color: "var(--dryvn-grey)" }}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="text-xs"
              style={{ color: "var(--dryvn-warm-deep)" }}
            >
              Delete
            </button>
          ))}
      </div>
      {error && (
        <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
          {error}
        </p>
      )}
    </form>
  );
}

type HomeworkFormValues = {
  what: string;
  kidMemberId: string;
  date: string;
  time: string;
};

/**
 * Minimal "Add homework" form (Schooling v1). Homework is create-only: it
 * queues one reminder on the platform's reminders rail — there is no
 * homework list backend, and the copy below says so honestly. Mirrors
 * EventForm's field components; the kid comes from the same household
 * roster the calendar uses.
 */
function HomeworkForm({
  kids,
  pending,
  error,
  onSubmit,
}: {
  kids: RosterMember[];
  pending: boolean;
  error: string | null;
  onSubmit: (v: HomeworkFormValues) => void;
}) {
  const [what, setWhat] = useState("");
  const [kidMemberId, setKidMemberId] = useState(kids[0]?.id ?? "");
  const [date, setDate] = useState(ymd(new Date()));
  const [time, setTime] = useState("16:00");

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const w = what.trim();
        if (!w || !kidMemberId) return;
        onSubmit({ what: w, kidMemberId, date, time });
      }}
    >
      <LifeField label="What's due">
        <LifeInput
          value={what}
          onChange={(e) => setWhat(e.target.value)}
          placeholder="Math worksheet, book report…"
        />
      </LifeField>
      <div className="grid grid-cols-2 gap-3">
        <LifeField label="Whose homework">
          <LifeSelect value={kidMemberId} onChange={(e) => setKidMemberId(e.target.value)}>
            {kids.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </LifeSelect>
        </LifeField>
        <LifeField label="Due date">
          <LifeInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </LifeField>
      </div>
      <LifeField label="Remind at">
        <LifeInput type="time" value={time} onChange={(e) => setTime(e.target.value)} />
      </LifeField>
      <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
        Homework reminders arrive by text — there's no homework list here yet.
      </p>
      <SubmitButton disabled={!what.trim() || !kidMemberId || pending}>
        {pending ? "Setting…" : "Set homework reminder"}
      </SubmitButton>
      {kids.length === 0 && (
        <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
          Add a family member first — the reminder names who it's for.
        </p>
      )}
      {error && (
        <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
          {error}
        </p>
      )}
    </form>
  );
}

/**
 * One event on the grid. Shows the time, title, and WHO's involved
 * (attendee avatars + names, or "Everyone" when nobody was singled out).
 * Tapping opens the edit form. Compact drops the who-line for the dense
 * month cells.
 */
function EventChip({
  event,
  roster,
  compact,
  onClick,
}: {
  event: FamilyEvent;
  roster: RosterMember[];
  compact?: boolean;
  onClick: () => void;
}) {
  const invited = event.attendeeIds
    .map((id) => roster.find((m) => m.id === id))
    .filter((m): m is RosterMember => Boolean(m));
  const names = invited.map((m) => m.name);
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-1 block w-full rounded-lg p-1.5 text-left leading-tight transition-opacity hover:opacity-80"
      style={{ backgroundColor: "var(--dryvn-card)", color: "var(--dryvn-ink)" }}
    >
      <div className="flex items-center gap-1">
        {!compact &&
          (invited.length > 0 ? (
            <span className="flex -space-x-1">
              {invited.slice(0, 3).map((m) => (
                <Avatar key={m.id} initials={m.initials} color={m.color} size={14} />
              ))}
            </span>
          ) : null)}
        <span className="font-mono text-[10px]" style={{ color: "var(--dryvn-grey)" }}>
          {event.time}
        </span>
      </div>
      <div className="mt-0.5 truncate text-[10px]">{event.title}</div>
      {!compact && (
        <div className="truncate text-[9px]" style={{ color: "var(--dryvn-grey)" }}>
          {names.length > 0 ? names.join(", ") : "Everyone"}
        </div>
      )}
    </button>
  );
}

function Family() {
  const { data: family } = useFamily();
  const { data: familyEvents, isError: eventsError, error: eventsErr } = useFamilyEvents();
  const { data: homework } = useHomework();
  const { data: rewardCharts } = useRewardCharts();
  const { data: behaviourLog } = useBehaviourLog();
  const { data: elders } = useElders();
  const addEvent = useAddFamilyEvent();
  const updateEvent = useUpdateFamilyEvent();
  const deleteEvent = useDeleteFamilyEvent();
  const { data: memberProfiles } = useFamilyMembers();
  const addMember = useAddFamilyMember();
  const updateMember = useUpdateFamilyMember();
  const deleteMember = useDeleteFamilyMember();
  const { data: me } = useMe();
  const isOwner = me?.role === "owner";
  const { data: pendingInvites } = usePendingInvites(isOwner && !IS_DEMO);
  const inviteMember = useInviteMember();
  const revokeInvite = useRevokeInvite();
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteKey, setInviteKey] = useState(0);

  const handleInvite = (v: { email: string; proposedRole: string }, close: () => void) => {
    setInviteError(null);
    inviteMember.mutate(v, {
      onSuccess: () => {
        setInviteKey((k) => k + 1);
        close();
      },
      onError: () =>
        setInviteError("We couldn't send that invite just now — give it another try in a moment."),
    });
  };
  const roster: RosterMember[] = [...family.parents, ...family.kids];
  const memberById = (id: string) => roster.find((m) => m.id === id);

  // Household profile forms: add-reset key + per-form error lines, and the
  // tap-to-edit target (same pattern as the calendar's event forms).
  const [memberAddKey, setMemberAddKey] = useState(0);
  const [memberAddError, setMemberAddError] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<WireMember | null>(null);
  const [memberEditError, setMemberEditError] = useState<string | null>(null);

  const handleAddMember = (v: FamilyMemberInput, close: () => void) => {
    setMemberAddError(null);
    addMember.mutate(v, {
      onSuccess: () => {
        setMemberAddKey((k) => k + 1);
        close();
      },
      onError: () =>
        setMemberAddError("We couldn't add them just now — give it another try in a moment."),
    });
  };

  const handleUpdateMember = (v: FamilyMemberInput) => {
    if (!editingMember) return;
    setMemberEditError(null);
    updateMember.mutate(
      { id: editingMember.id, ...v },
      {
        onSuccess: () => setEditingMember(null),
        onError: () =>
          setMemberEditError("We couldn't save that change just now — give it another try."),
      },
    );
  };

  const handleDeleteMember = () => {
    if (!editingMember) return;
    setMemberEditError(null);
    deleteMember.mutate(editingMember.id, {
      onSuccess: () => setEditingMember(null),
      onError: () => setMemberEditError("We couldn't remove them just now — give it another try."),
    });
  };

  // Optimistic events so a web add/edit/delete shows in the grid at once.
  const [events, setEvents] = useState<FamilyEvent[]>(familyEvents);
  useEffect(() => setEvents(familyEvents), [familyEvents]);

  // School section (Schooling v1): homework = one reminder on the platform
  // reminders rail (create-only, arrives by text). Confirmation is shown
  // inline because there is no homework list to refetch.
  const addHomework = useAddHomework();
  const [homeworkKey, setHomeworkKey] = useState(0);
  const [homeworkError, setHomeworkError] = useState<string | null>(null);
  const [homeworkConfirm, setHomeworkConfirm] = useState<string | null>(null);

  const handleAddHomework = (v: HomeworkFormValues, close: () => void) => {
    setHomeworkError(null);
    addHomework.mutate(
      { what: v.what, kidMemberId: v.kidMemberId, remindAt: isoFromDateTime(v.date, v.time) },
      {
        onSuccess: (hw) => {
          setHomeworkConfirm(
            `${hw.reminderText} — reminder set for ${shortDateLabel(v.date)} at ${v.time}. It'll arrive by text.`,
          );
          setHomeworkKey((k) => k + 1);
          close();
        },
        onError: () =>
          setHomeworkError(
            "We couldn't set that reminder just now — give it another try in a moment.",
          ),
      },
    );
  };

  // Calendar view: week vs month, anchored to a reference date that prev/next
  // steps by one week or one month.
  const [viewMode, setViewMode] = useState<"week" | "month">("week");
  const [anchor, setAnchor] = useState<Date>(() => new Date());

  // Add-form reset key (bumped on a successful add) + its own error line.
  const [addKey, setAddKey] = useState(0);
  const [addError, setAddError] = useState<string | null>(null);

  // Tap-to-edit target + its error line.
  const [editing, setEditing] = useState<FamilyEvent | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const todayYmd = ymd(new Date());

  // Group the (optimistic) events by local date for O(1) cell lookups.
  const eventsByDate = new Map<string, FamilyEvent[]>();
  for (const e of events) {
    const list = eventsByDate.get(e.date) ?? [];
    list.push(e);
    eventsByDate.set(e.date, list);
  }
  const eventsOn = (d: Date) =>
    (eventsByDate.get(ymd(d)) ?? []).slice().sort((a, b) => a.time.localeCompare(b.time));

  // Upcoming school events — the calendar's own rows (eventKind='school'),
  // next 30 days, soonest first. Same data the grid renders; no new fetch.
  const schoolWindowEnd = ymd(addDays(new Date(), 30));
  const schoolEvents = events
    .filter((e) => e.kind === "school" && e.date >= todayYmd && e.date <= schoolWindowEnd)
    .sort((a, b) =>
      a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date),
    );

  const weekStart = atStartOfWeek(anchor);
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const monthGridStart = atStartOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  const monthDates = Array.from({ length: 42 }, (_, i) => addDays(monthGridStart, i));

  const rangeLabel =
    viewMode === "week"
      ? `${weekDates[0]!.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })} – ${weekDates[6]!.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const step = (dir: -1 | 1) =>
    setAnchor((a) => (viewMode === "week" ? addDays(a, dir * 7) : addMonths(a, dir)));

  // Add: optimistic insert; roll back on error (onSuccess-only confirm).
  const handleAdd = (v: EventFormValues, close: () => void) => {
    const startsAt = isoFromDateTime(v.date, v.time);
    const dt = new Date(startsAt);
    const snapshot = events;
    setEvents((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        title: v.title,
        memberId: v.attendeeIds[0] ?? "",
        day: (dt.getDay() + 6) % 7,
        time: v.time,
        kind: WIRE_TO_VIEW[v.kind] ?? "family",
        date: v.date,
        startsAt,
        attendeeIds: v.attendeeIds,
      },
    ]);
    setAddError(null);
    addEvent.mutate(
      {
        title: v.title,
        startsAt,
        eventKind: v.kind as "family" | "school" | "sports" | "appointment" | "carpool",
        ...(v.attendeeIds.length ? { attendeeMemberIds: v.attendeeIds } : {}),
      },
      {
        onSuccess: () => {
          setAddKey((k) => k + 1);
          close();
        },
        onError: () => {
          setEvents(snapshot);
          setAddError("We couldn't add that just now — give it another try in a moment.");
        },
      },
    );
  };

  // Edit: optimistic replace; roll back on error.
  const handleUpdate = (v: EventFormValues) => {
    if (!editing) return;
    const id = editing.id;
    const startsAt = isoFromDateTime(v.date, v.time);
    const dt = new Date(startsAt);
    const snapshot = events;
    setEvents((prev) =>
      prev.map((e) =>
        e.id === id
          ? {
              ...e,
              title: v.title,
              memberId: v.attendeeIds[0] ?? "",
              day: (dt.getDay() + 6) % 7,
              time: v.time,
              kind: WIRE_TO_VIEW[v.kind] ?? "family",
              date: v.date,
              startsAt,
              attendeeIds: v.attendeeIds,
            }
          : e,
      ),
    );
    setEditError(null);
    updateEvent.mutate(
      {
        id,
        title: v.title,
        startsAt,
        eventKind: v.kind as "family" | "school" | "sports" | "appointment" | "carpool",
        attendeeMemberIds: v.attendeeIds,
      },
      {
        onSuccess: () => setEditing(null),
        onError: () => {
          setEvents(snapshot);
          setEditError("We couldn't save that change just now — give it another try.");
        },
      },
    );
  };

  // Delete (soft): optimistic removal; roll back on error.
  const handleDelete = () => {
    if (!editing) return;
    const id = editing.id;
    const snapshot = events;
    setEvents((prev) => prev.filter((e) => e.id !== id));
    setEditError(null);
    deleteEvent.mutate(id, {
      onSuccess: () => setEditing(null),
      onError: () => {
        setEvents(snapshot);
        setEditError("We couldn't delete that just now — give it another try.");
      },
    });
  };

  const openEdit = (e: FamilyEvent) => {
    setEditError(null);
    setEditing(e);
  };

  return (
    <AppShell>
      <HeroCard
        eyebrow="Household"
        title={
          <>
            The{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              whole
            </em>{" "}
            family.
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-4">
          {[...family.parents, ...family.kids].map((m) => (
            <div key={m.id} className="flex items-center gap-2">
              <Avatar initials={m.initials} color={m.color} size={38} />
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--dryvn-ink)" }}>
                  {m.name}
                </p>
                {"age" in m && (
                  <p className="text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
                    age {String(m.age)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </HeroCard>

      <div className="mt-8">
        <MagazineGrid>
          {/* Household — member profiles, the Life face of the Team roster. */}
          <section className="lg:col-span-6">
            <SectionHeader eyebrow="Household" title="Who's in the family" />

            <div className="mb-4">
              <AddPanel label="Add a family member">
                {(close) => (
                  <MemberForm
                    key={`member-add-${memberAddKey}`}
                    initial={{
                      name: "",
                      relationship: "parent",
                      dateOfBirth: "",
                      phone: "",
                      email: "",
                    }}
                    submitLabel="Add member"
                    pending={addMember.isPending}
                    error={memberAddError}
                    onSubmit={(v) => handleAddMember(v, close)}
                  />
                )}
              </AddPanel>
            </div>

            {editingMember && (
              <div className="mb-4">
                <DCard>
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-display text-lg" style={{ color: "var(--dryvn-ink)" }}>
                      Edit profile
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditingMember(null)}
                      className="grid h-8 w-8 place-items-center rounded-full"
                      style={{ color: "var(--dryvn-grey)" }}
                      aria-label="Close profile editor"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <MemberForm
                    key={`member-edit-${editingMember.id}`}
                    initial={{
                      name: editingMember.name,
                      relationship: RELATIONSHIP_OPTIONS.some(
                        (o) => o.value === editingMember.relationship,
                      )
                        ? editingMember.relationship
                        : "other",
                      dateOfBirth: editingMember.dateOfBirth ?? "",
                      phone: editingMember.phone ?? "",
                      email: editingMember.email ?? "",
                    }}
                    submitLabel="Save profile"
                    pending={updateMember.isPending}
                    error={memberEditError}
                    onSubmit={handleUpdateMember}
                    onDelete={handleDeleteMember}
                    deleting={deleteMember.isPending}
                  />
                </DCard>
              </div>
            )}

            {memberProfiles.length === 0 ? (
              <EmptyState
                title="No family members yet. Add the household above — profiles power the calendar, check-ins, and events."
                smsExamples={[
                  "add family member Ellie, my daughter",
                  "add Grandma Rose to the family",
                ]}
              />
            ) : (
              <CollapsibleList items={memberProfiles} resource="members">
                {(shownMembers) => (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {shownMembers.map((m, i) => (
                      <button
                        type="button"
                        key={m.id}
                        onClick={() => {
                          setMemberEditError(null);
                          setEditingMember(m);
                        }}
                        className="text-left"
                      >
                        <DCard className="flex items-center gap-3 transition-opacity hover:opacity-85">
                          <Avatar
                            initials={memberInitials(m.name)}
                            color={memberColor(i)}
                            size={38}
                          />
                          <div className="min-w-0 flex-1">
                            <p
                              className="truncate text-sm font-medium"
                              style={{ color: "var(--dryvn-ink)" }}
                            >
                              {m.name}
                            </p>
                            <p
                              className="truncate text-[11px]"
                              style={{ color: "var(--dryvn-grey)" }}
                            >
                              {relationshipLabel(m.relationship)}
                              {m.dateOfBirth ? ` · b. ${m.dateOfBirth}` : ""}
                              {m.phone ? ` · ${m.phone}` : ""}
                            </p>
                          </div>
                        </DCard>
                      </button>
                    ))}
                  </div>
                )}
              </CollapsibleList>
            )}

            {/* App access — owner-only. Consent-gated: email first, the
                invited person consents + opts in themselves. */}
            {isOwner && !IS_DEMO && (
              <div className="mt-6">
                <SectionHeader eyebrow="App access" title="Invite the family in" />
                <div className="mb-4">
                  <AddPanel label="Invite to the app">
                    {(close) => (
                      <InviteForm
                        key={`invite-${inviteKey}`}
                        pending={inviteMember.isPending}
                        error={inviteError}
                        onSubmit={(v) => handleInvite(v, close)}
                      />
                    )}
                  </AddPanel>
                </div>
                {pendingInvites.length > 0 && (
                  <DCard className="p-4">
                    <p
                      className="mb-1 font-mono text-[10px] uppercase tracking-wider"
                      style={{ color: "var(--dryvn-grey)" }}
                    >
                      Waiting on them
                    </p>
                    <CollapsibleList items={pendingInvites} resource="invites">
                      {(shownInvites) => (
                        <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                          {shownInvites.map((inv) => (
                            <PendingInviteRow
                              key={inv.id}
                              invite={inv}
                              revoking={revokeInvite.isPending}
                              onRevoke={() => revokeInvite.mutate(inv.id)}
                            />
                          ))}
                        </ul>
                      )}
                    </CollapsibleList>
                  </DCard>
                )}
              </div>
            )}
          </section>

          {/* School — Schooling v1, LIVE in both modes. Upcoming school
              events come straight from the calendar's rows (kind School);
              homework queues a text reminder on the platform reminders
              rail (create-only — no homework list backend exists). */}
          <section className="lg:col-span-6">
            <SectionHeader eyebrow="School" title="The school week" />

            <div className="mb-4">
              <AddPanel label="Add homework">
                {(close) => (
                  <HomeworkForm
                    key={`homework-${homeworkKey}`}
                    kids={family.kids.length > 0 ? family.kids : roster}
                    pending={addHomework.isPending}
                    error={homeworkError}
                    onSubmit={(v) => handleAddHomework(v, close)}
                  />
                )}
              </AddPanel>
            </div>

            {homeworkConfirm && (
              <div className="mb-4">
                <DCard className="flex items-start gap-2 p-4">
                  <Check
                    className="mt-0.5 h-4 w-4 shrink-0"
                    style={{ color: "var(--dryvn-primary)" }}
                  />
                  <p className="text-sm" style={{ color: "var(--dryvn-ink)" }}>
                    {homeworkConfirm}
                  </p>
                </DCard>
              </div>
            )}

            {schoolEvents.length === 0 ? (
              <EmptyState
                title="No school events in the next 30 days. Add one on the calendar — pick kind School — or text your Dryvn number."
                smsExamples={["Henry's field trip Thursday 9am"]}
              />
            ) : (
              <>
                <DCard className="p-4">
                  <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                    {schoolEvents.map((e) => {
                      const names = attendeeNames(e.attendeeIds, roster);
                      return (
                        <li key={e.id} className="flex items-center gap-3 py-2.5">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm" style={{ color: "var(--dryvn-ink)" }}>
                              {e.title}
                            </p>
                            <p className="text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
                              {names.length > 0 ? names.join(", ") : "Everyone"}
                            </p>
                          </div>
                          <Pill tone="muted">
                            {shortDateLabel(e.date)} · {e.time}
                          </Pill>
                        </li>
                      );
                    })}
                  </ul>
                </DCard>
                <p className="mt-2 text-xs" style={{ color: "var(--dryvn-grey)" }}>
                  Add or edit school events with the calendar's Add panel — pick kind School.
                </p>
              </>
            )}
          </section>

          {/* Circle share — demo-only: fixture broadcast + placeholder number. */}
          {IS_DEMO && (
            <section className="lg:col-span-6">
              <SectionHeader eyebrow="Circle" title="Text everyone" />
              <DCard className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                    Last broadcast · {lastBroadcast.sentAt}
                  </p>
                  <p
                    className="mt-1 font-serif italic text-base leading-snug"
                    style={{ color: "var(--dryvn-ink)", fontFamily: "var(--font-display)" }}
                  >
                    “{lastBroadcast.text}”
                  </p>
                  <div
                    className="mt-2 flex items-center gap-3 text-xs"
                    style={{ color: "var(--dryvn-grey)" }}
                  >
                    <span className="inline-flex items-center gap-1">
                      <MessageCircle className="h-3 w-3" /> {lastBroadcast.replies} replies
                    </span>
                  </div>
                </div>
                <a
                  href={`sms:${DRYVN_NUMBER}?body=broadcast:`}
                  className="inline-flex shrink-0 items-center justify-center rounded-full px-4 py-2 text-sm font-medium text-white"
                  style={{ backgroundColor: "var(--dryvn-primary)" }}
                >
                  Text everyone
                </a>
              </DCard>
            </section>
          )}

          {/* Calendar — week / month toggle, per-event attendees, tap to edit */}
          <section className="lg:col-span-6">
            <SectionHeader
              eyebrow="Calendar"
              title="The family's plans"
              action={
                <div
                  className="inline-flex rounded-full border p-0.5"
                  style={{ borderColor: "var(--dryvn-line)" }}
                >
                  {(["week", "month"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setViewMode(m)}
                      className="rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors"
                      style={
                        viewMode === m
                          ? { backgroundColor: "var(--dryvn-primary)", color: "#fff" }
                          : { color: "var(--dryvn-grey)" }
                      }
                    >
                      {m}
                    </button>
                  ))}
                </div>
              }
            />

            {/* Web entry — a second way in besides SMS. */}
            <div className="mb-4">
              <AddPanel label="Add to the calendar">
                {(close) => (
                  <EventForm
                    key={`add-${addKey}`}
                    roster={roster}
                    initial={{
                      title: "",
                      date: ymd(anchor),
                      time: "17:00",
                      kind: "family",
                      attendeeIds: [],
                    }}
                    submitLabel="Add event"
                    pending={addEvent.isPending}
                    error={addError}
                    onSubmit={(v) => handleAdd(v, close)}
                  />
                )}
              </AddPanel>
            </div>

            {/* Edit — reuses the add form; attendees editable, delete with confirm. */}
            {editing && (
              <div className="mb-4">
                <DCard>
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-display text-lg" style={{ color: "var(--dryvn-ink)" }}>
                      Edit event
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="grid h-8 w-8 place-items-center rounded-full"
                      style={{ color: "var(--dryvn-grey)" }}
                      aria-label="Close editor"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <EventForm
                    key={`edit-${editing.id}`}
                    roster={roster}
                    initial={{
                      title: editing.title,
                      date: editing.date,
                      time: editing.time,
                      kind: VIEW_TO_WIRE[editing.kind] ?? "family",
                      attendeeIds: editing.attendeeIds,
                    }}
                    submitLabel="Save changes"
                    pending={updateEvent.isPending}
                    error={editError}
                    onSubmit={handleUpdate}
                    onDelete={handleDelete}
                    deleting={deleteEvent.isPending}
                  />
                </DCard>
              </div>
            )}

            {/* Range nav */}
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => step(-1)}
                className="grid h-8 w-8 place-items-center rounded-full border"
                style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-ink)" }}
                aria-label="Previous"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-3">
                <span className="font-display text-base" style={{ color: "var(--dryvn-ink)" }}>
                  {rangeLabel}
                </span>
                <button
                  type="button"
                  onClick={() => setAnchor(new Date())}
                  className="text-xs"
                  style={{ color: "var(--dryvn-primary)" }}
                >
                  Today
                </button>
              </div>
              <button
                type="button"
                onClick={() => step(1)}
                className="grid h-8 w-8 place-items-center rounded-full border"
                style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-ink)" }}
                aria-label="Next"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {eventsError && <LoadError error={eventsErr} />}
            {!eventsError && events.length === 0 && (
              <div className="mb-4">
                <EmptyState
                  title="Nothing on the family calendar yet. Add an event above, or text your Dryvn number."
                  smsExamples={["soccer Thursday 5pm for Ellie", "dentist Tuesday 9am"]}
                />
              </div>
            )}

            {viewMode === "week" ? (
              <DCard className="p-4">
                <div
                  className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium"
                  style={{ color: "var(--dryvn-grey)" }}
                >
                  {weekDates.map((d) => (
                    <div key={ymd(d)}>
                      <div>{days[(d.getDay() + 6) % 7]}</div>
                      <div
                        className="mt-0.5 font-mono text-[11px]"
                        style={{
                          color: ymd(d) === todayYmd ? "var(--dryvn-primary)" : "var(--dryvn-ink)",
                        }}
                      >
                        {d.getDate()}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-7 gap-1">
                  {weekDates.map((d) => {
                    // Same per-cell cap the month grid uses below: a heavy day
                    // must not stretch the whole row. The overflow is a count,
                    // not a hidden list — the day view shows every event.
                    const dayEvents = eventsOn(d);
                    return (
                      <div
                        key={ymd(d)}
                        className="min-h-[110px] rounded-xl p-1.5"
                        style={{ backgroundColor: "var(--dryvn-page-tint)" }}
                      >
                        {dayEvents.slice(0, DAY_CELL_CAP).map((e) => (
                          <EventChip
                            key={e.id}
                            event={e}
                            roster={roster}
                            onClick={() => openEdit(e)}
                          />
                        ))}
                        {dayEvents.length > DAY_CELL_CAP && (
                          <div className="px-1 text-[9px]" style={{ color: "var(--dryvn-grey)" }}>
                            +{dayEvents.length - DAY_CELL_CAP} more
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </DCard>
            ) : (
              <DCard className="p-4">
                <div
                  className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium"
                  style={{ color: "var(--dryvn-grey)" }}
                >
                  {days.map((d) => (
                    <div key={d}>{d}</div>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-7 gap-1">
                  {monthDates.map((d) => {
                    const inMonth = d.getMonth() === anchor.getMonth();
                    const dayEvents = eventsOn(d);
                    return (
                      <div
                        key={ymd(d)}
                        className="min-h-[84px] rounded-xl p-1"
                        style={{
                          backgroundColor: "var(--dryvn-page-tint)",
                          opacity: inMonth ? 1 : 0.45,
                        }}
                      >
                        <div
                          className="mb-0.5 text-right font-mono text-[10px]"
                          style={{
                            color:
                              ymd(d) === todayYmd ? "var(--dryvn-primary)" : "var(--dryvn-grey)",
                          }}
                        >
                          {d.getDate()}
                        </div>
                        {dayEvents.slice(0, DAY_CELL_CAP).map((e) => (
                          <EventChip
                            key={e.id}
                            event={e}
                            roster={roster}
                            compact
                            onClick={() => openEdit(e)}
                          />
                        ))}
                        {dayEvents.length > DAY_CELL_CAP && (
                          <div className="px-1 text-[9px]" style={{ color: "var(--dryvn-grey)" }}>
                            +{dayEvents.length - DAY_CELL_CAP} more
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </DCard>
            )}
          </section>

          {/* Elders / Homework / Rewards / Behaviour — Phase-12 DEFER, no v1
              backend (empty in live). Demo-only until those ship. */}
          {IS_DEMO && (
            <>
              {/* Elder check-ins */}
              <section className="lg:col-span-6">
                <SectionHeader eyebrow="Elders" title="Check-ins" />
                <div className="grid gap-4 sm:grid-cols-2">
                  {elders.map((e) => (
                    <DCard
                      key={e.id}
                      className={e.missed ? "" : ""}
                      style={
                        e.missed
                          ? {
                              borderColor:
                                "color-mix(in oklab, var(--dryvn-amber) 55%, transparent)",
                              backgroundColor:
                                "color-mix(in oklab, var(--dryvn-amber) 10%, var(--dryvn-card))",
                            }
                          : undefined
                      }
                    >
                      <div className="flex items-start gap-3">
                        <Avatar initials={e.initials} color={e.color} size={44} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                                {e.name}
                              </p>
                              <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                                {e.relation}
                              </p>
                            </div>
                            {e.missed ? (
                              <Pill tone="amber">needs a nudge</Pill>
                            ) : (
                              <Pill tone="life">
                                <Heart className="mr-1 h-3 w-3" /> {e.streak}d streak
                              </Pill>
                            )}
                          </div>
                          <p
                            className="mt-3 text-sm leading-snug"
                            style={{
                              color: e.missed ? "var(--dryvn-warm-deep)" : "var(--dryvn-ink)",
                            }}
                          >
                            {e.missed ? e.missedFor : `Last: ${e.lastCheckin}`}
                          </p>
                        </div>
                      </div>
                    </DCard>
                  ))}
                </div>
              </section>

              {/* Homework */}
              <section className="lg:col-span-3">
                <SectionHeader eyebrow="School" title="Homework" />
                <div className="space-y-4">
                  {family.kids.map((kid) => {
                    const items = homework.filter((h) => h.kidId === kid.id);
                    return (
                      <DCard key={kid.id}>
                        <div className="mb-3 flex items-center gap-2">
                          <Avatar initials={kid.initials} color={kid.color} size={26} />
                          <span className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                            {kid.name}
                          </span>
                        </div>
                        <ul className="space-y-2">
                          {items.map((h) => (
                            <li key={h.id} className="flex items-center gap-2 text-sm">
                              <span
                                className="grid h-4 w-4 place-items-center rounded border"
                                style={{
                                  borderColor: h.done
                                    ? "var(--dryvn-primary)"
                                    : "var(--dryvn-line)",
                                  backgroundColor: h.done ? "var(--dryvn-primary)" : "transparent",
                                }}
                              >
                                {h.done && <Check className="h-3 w-3 text-white" />}
                              </span>
                              <span
                                className="flex-1"
                                style={{
                                  color: h.done ? "var(--dryvn-grey)" : "var(--dryvn-ink)",
                                  textDecoration: h.done ? "line-through" : "none",
                                }}
                              >
                                <span
                                  className="mr-1 font-mono text-xs"
                                  style={{ color: "var(--dryvn-grey)" }}
                                >
                                  {h.subject}
                                </span>
                                {h.title}
                              </span>
                              <Pill tone="muted">{h.dueDate}</Pill>
                            </li>
                          ))}
                        </ul>
                      </DCard>
                    );
                  })}
                </div>
              </section>

              {/* Reward charts */}
              <section className="lg:col-span-3">
                <SectionHeader eyebrow="Rewards" title="Charts" />
                <div className="space-y-4">
                  {rewardCharts.map((r) => {
                    const kid = memberById(r.kidId);
                    return (
                      <DCard key={r.id}>
                        <div className="mb-2 flex items-center gap-2">
                          {kid && <Avatar initials={kid.initials} color={kid.color} size={22} />}
                          <span
                            className="text-sm font-medium"
                            style={{ color: "var(--dryvn-ink)" }}
                          >
                            {r.name}
                          </span>
                        </div>
                        <ProgressBar value={r.points} max={r.target} tint="life" />
                        <div
                          className="mt-2 flex items-center justify-between text-xs"
                          style={{ color: "var(--dryvn-grey)" }}
                        >
                          <span>
                            <span className="font-mono" style={{ color: "var(--dryvn-ink)" }}>
                              {r.points}
                            </span>{" "}
                            / {r.target}
                          </span>
                          <span>🎁 {r.reward}</span>
                        </div>
                      </DCard>
                    );
                  })}
                </div>
              </section>

              {/* Behaviour log — private */}
              <section className="lg:col-span-6">
                <SectionHeader
                  eyebrow="Private"
                  title="Behaviour log"
                  action={
                    <span
                      className="flex items-center gap-1 text-[11px]"
                      style={{ color: "var(--dryvn-grey)" }}
                    >
                      <Lock className="h-3 w-3" /> encrypted · parents only
                    </span>
                  }
                />
                <DCard className="p-4">
                  <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                    {behaviourLog.map((e) => {
                      const kid = memberById(e.kidId);
                      return (
                        <li key={e.id} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                          <span
                            className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: toneStyle[e.tone] }}
                          />
                          <div className="flex-1">
                            <div
                              className="flex items-center gap-2 text-xs"
                              style={{ color: "var(--dryvn-grey)" }}
                            >
                              <span className="font-mono">{e.date}</span>
                              <span>·</span>
                              <span>{kid?.name}</span>
                              <span>·</span>
                              <span>{e.tone}</span>
                            </div>
                            <p className="mt-1 text-sm" style={{ color: "var(--dryvn-ink)" }}>
                              {e.note}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </DCard>
              </section>
            </>
          )}
        </MagazineGrid>
      </div>
    </AppShell>
  );
}
